// Core
import { useCallback, useState } from 'react';
import {  useQuery } from 'react-query';

// Chakra
import {

  Text,
  useColorModeValue,
  Divider,
  Button,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  Menu,
  MenuButton,
  MenuList,
  Box,
  MenuItem,
  InputGroup,
  InputLeftElement, Input, FormControl
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import {filterDate, parseDateTime} from '../../../services/Utils';
import {DateTimeIcon, FilterIcon} from '../../../components/Icons/Icons';
import DatePicker from 'react-datepicker';
import {useDebounce} from 'use-debounce';
import {SearchIcon} from '@chakra-ui/icons';

const PosTransaction = () => {

  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [posTransactions, setPosTransactions] = useState([]);
  const [posTransactionsMeta, setPosTransactionsMeta] = useState([]);
  const limit = 25;

  const [saleType, setSaleType] = useState('');

  const [filterButtonLabel, setFilterButtonLabel] = useState('All Time');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [search, setSearch] = useState();
  const [debouncedSearch] = useDebounce(search, 500);

  const { isFetching, refetch } = useQuery({
    queryKey: ['pos-transactions', offset, startDate, endDate, saleType, debouncedSearch],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const params = {
        limit,
        offset,
      };

      if (startDate) {
        params['from'] = new Date(startDate).toISOString();
      }

      if (endDate) {
        params['to'] = new Date(endDate).toISOString();
      }

      if(saleType) {
        params['saleType'] = saleType;
      }

      if(debouncedSearch) {
        params['barcode'] = debouncedSearch;
      }

      const response = await apiService.getPosTransactions(params);
      setPosTransactions(response.data);
      setPosTransactionsMeta(response.meta);
      return true;
    },
    retry: false,
    refetchInterval: 30000,
  });


  const handleEditOpen = useCallback(
    (plan) => {
      history.push(`/pos-transactions/${plan.id}`);
    },
    [history],
  );

  const saleTypeOptions = {
    '': 'All Types',
    'FUEL_ONLY': 'FUEL ONLY',
    'RETAIL_ONLY': 'RETAIL ONLY',
    'FUEL_AND_RETAIL': 'FUEL AND RETAIL',
  };

  const handleSaleTypeButton = (type) => {
    if (type !== saleType) {
      setSaleType(type);
    }
  };

  const handleDate = (dates) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);
  };

  const handleDateButton = (type) => {
    setFilterButtonLabel(type);
    const data = filterDate(type);
    handleDate(data);
  };


  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize='40px' fontStyle='normal' fontWeight='700' color={textColor} mb='0px' className="page-name">POS Transactions</Text>
      </Flex>

      <Flex gap={2} py={1}>

        <FormControl
            variant="search"
            maxW="274px"
            className={search?.length > 0 ? 'search-active' : ''}
        >
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon fontSize="24px" ml="8px" />
            </InputLeftElement>
            <Input type="text" placeholder="Barcode" onChange={(e)=> setSearch(e.target.value)} value={search} />
          </InputGroup>
        </FormControl>

        <Menu variant="filter">
          <MenuButton
              as={Button}
              leftIcon={
                <DateTimeIcon color="white" fontSize="24px" ml="0px" mr="0px" />
              }
              variant="secondary-md"
              color="white"
              mt="-2px"
              w="200px"
              px="24px !important"
          >
            {filterButtonLabel}
          </MenuButton>
          <MenuList className="context-menu filter-date">
            <Box>
              <DatePicker
                  onChange={handleDate}
                  selected={startDate}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  locale="en"
                  dateFormat="d/MM/yyyy"
              />
              {!startDate && !endDate && (
                  <Text
                      position="absolute"
                      variant="text"
                      className="filter-date-placeholder"
                      pointerEvents="none"
                  >
                    Enter a date range
                  </Text>
              )}
              <DateTimeIcon
                  fontSize="24px"
                  position="absolute"
                  right="20px"
                  top="17px"
                  color="#9FABCB"
                  pointerEvents="none"
              />
            </Box>
            <MenuItem onClick={() => handleDateButton('All Time')}>
              All Time
            </MenuItem>
            <MenuItem onClick={() => handleDateButton('This Month')}>
              This Month
            </MenuItem>
            <MenuItem onClick={() => handleDateButton('Last Month')}>
              Last Month
            </MenuItem>
            <MenuItem onClick={() => handleDateButton('Last 3 Month')}>
              Last 3 Month
            </MenuItem>
            <MenuItem onClick={() => handleDateButton('This Year')}>
              This Year
            </MenuItem>
          </MenuList>
        </Menu>

        <Menu variant="filter">
          <MenuButton
              as={Button}
              leftIcon={
                <FilterIcon color="white" fontSize="24px" ml="0px" mr="0px" />
              }
              variant="secondary-md"
              color="white"
              mt="-2px"
              w="220px"
              px="24px !important"
          >
            {saleTypeOptions[saleType]}
          </MenuButton>
          <MenuList className="context-menu">
            {Object.entries(saleTypeOptions).map(([value, label]) => (
                <MenuItem
                    key={value}
                    onClick={() => handleSaleTypeButton(value)}
                >
                  {label}
                </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      <Divider opacity="1" mr="2px" borderColor={borderColor} borderTopWidth="1px" borderBottomWidth="0" mb='32px'/>

      {isFetching ? (
        <TableSkeleton rowCount={5} />
      )
      :posTransactions.length > 0 ? (
        <Card padding={'8px'}>
          <Table color={textColor}>
            <Thead>
              <Tr>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Store ID
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Terminal ID
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Sale Type
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Contract Level
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    GST Total
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Currency
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Timestamp UTC
                  </Text>
                </Td>
                <Td>
                </Td>
              </Tr>
            </Thead>
            <Tbody>
              {posTransactions.map((row) => {
                return (
                  <Tr
                    onClick={() => handleEditOpen(row)}
                    _last={{
                    '& td': {
                      borderBottom: 'none !important',
                    },
                  }} key={row.id} border="unset" className="cursor-tr">
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.storeId}
                      </Text>
                    </Td>
                    <Td  verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.terminalId}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.saleType}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.contractLevel}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.gstTotal}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.currency}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.timestampUtc}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Button
                        variant="submit-ms"
                        maxW="140px"
                        w="100%"
                        onClick={handleEditOpen}
                      >
                        View details
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Card>
      ) : (
        <NotFound text="Not Found" />
      )}

      <Pagination
        meta={posTransactionsMeta ?? { limit, offset, total: 0 }}
        isLoadingPage={isFetching}
        currentPage={Math.floor(offset / limit) + 1}
        onPageChange={(newOffset) => setOffset(newOffset)}
      />
      {/*<Attention*/}
      {/*  variant="remove"*/}
      {/*  handle={() => handleDelete(object)}*/}
      {/*  isOpen={isOpen}*/}
      {/*  onOpen={onOpen}*/}
      {/*  onClose={onClose}*/}
      {/*/>*/}
    </>
  );
};

export default PosTransaction;
